import { useEffect, useMemo, useRef, type CSSProperties, type ReactNode } from 'react';
import {
  isSafeSystemMessageHref,
  isSafeSystemMessageImageUrl,
  SYSTEM_MESSAGE_IMAGE_LIGHTBOX_ATTR,
  SYSTEM_MESSAGE_IMAGE_LIGHTBOX_SRC_ATTR,
} from '@fdh/shared';
import { useLanguage } from '../../contexts/LanguageContext';

type SystemMessageHtmlContentProps = {
  html: string;
  emptyFallback: string;
  onOpenImage: (src: string) => void;
};

const LIGHTBOX_BUTTON_CLASS =
  'inline cursor-zoom-in touch-manipulation bg-transparent p-0 text-left font-[inherit] text-inherit text-primary-700 underline underline-offset-2 dark:text-primary-400';

const IMAGE_FILE_EXT_RE = /\.(avif|bmp|gif|jpe?g|png|svg|webp)(?:\?|#|$)/i;

const hrefLooksLikeImageFile = (href: string): boolean =>
  isSafeSystemMessageImageUrl(href) && IMAGE_FILE_EXT_RE.test(href);

const eventTargetElement = (target: EventTarget | null): Element | null => {
  if (target instanceof Element) {
    return target;
  }
  if (target instanceof Node) {
    return target.parentElement;
  }
  return null;
};

const parseMaxBoxStyle = (raw: string | null): CSSProperties | undefined => {
  if (!raw) {
    return undefined;
  }
  const style: CSSProperties = {};
  raw.split(';').forEach((part) => {
    const colon = part.indexOf(':');
    if (colon <= 0) {
      return;
    }
    const prop = part.slice(0, colon).trim().toLowerCase();
    const value = part.slice(colon + 1).trim();
    if (prop === 'max-width') {
      style.maxWidth = value;
    }
    if (prop === 'max-height') {
      style.maxHeight = value;
    }
  });
  if (!style.maxWidth && !style.maxHeight) {
    return undefined;
  }
  return style;
};

const lightboxSrcFrom = (el: Element): string => {
  const fromData = el.getAttribute(SYSTEM_MESSAGE_IMAGE_LIGHTBOX_SRC_ATTR)?.trim() ?? '';
  if (fromData) {
    return fromData;
  }
  if (el.tagName.toLowerCase() === 'a') {
    return el.getAttribute('href')?.trim() ?? '';
  }
  if (el.tagName.toLowerCase() === 'img') {
    return el.getAttribute('src')?.trim() ?? '';
  }
  return '';
};

const isLightboxHost = (el: Element): boolean => {
  if (!el.hasAttribute(SYSTEM_MESSAGE_IMAGE_LIGHTBOX_ATTR)) {
    return false;
  }
  return isSafeSystemMessageImageUrl(lightboxSrcFrom(el));
};

const parseRootNodes = (html: string): ChildNode[] => {
  if (typeof DOMParser === 'undefined' || html.trim() === '') {
    return [];
  }
  const doc = new DOMParser().parseFromString(`<div id="sysmsg-html-root">${html}</div>`, 'text/html');
  const root = doc.getElementById('sysmsg-html-root');
  if (!root) {
    return [];
  }
  return Array.from(root.childNodes);
};

export const SystemMessageHtmlContent = ({
  html,
  emptyFallback,
  onOpenImage,
}: SystemMessageHtmlContentProps) => {
  const { t } = useLanguage();
  const containerRef = useRef<HTMLDivElement>(null);
  const onOpenImageRef = useRef(onOpenImage);
  onOpenImageRef.current = onOpenImage;

  const rootNodes = useMemo(() => parseRootNodes(html), [html]);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) {
      return undefined;
    }
    const intercept = (event: Event): void => {
      const target = eventTargetElement(event.target);
      if (!target || !root.contains(target)) {
        return;
      }
      const host = target.closest(`[${SYSTEM_MESSAGE_IMAGE_LIGHTBOX_ATTR}]`);
      if (host instanceof HTMLElement && root.contains(host)) {
        const src = lightboxSrcFrom(host);
        if (isSafeSystemMessageImageUrl(src)) {
          event.preventDefault();
          event.stopPropagation();
          onOpenImageRef.current(src);
          return;
        }
      }
      const anchor = target.closest('a');
      if (anchor instanceof HTMLAnchorElement && root.contains(anchor)) {
        if (anchor.getElementsByTagName('img').length > 0) {
          return;
        }
        const href = anchor.getAttribute('href') ?? '';
        if (hrefLooksLikeImageFile(href)) {
          event.preventDefault();
          event.stopPropagation();
          onOpenImageRef.current(href);
        }
      }
    };
    root.addEventListener('click', intercept, true);
    return () => {
      root.removeEventListener('click', intercept, true);
    };
  }, []);

  const renderNode = (
    node: ChildNode,
    index: number,
    insideLightbox: boolean,
    insideAnchor: boolean,
  ): ReactNode => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }
    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    const href = tag === 'a' ? (el.getAttribute('href') ?? '') : '';
    const lightboxHost = isLightboxHost(el);
    const hasImgChild = tag === 'a' && el.getElementsByTagName('img').length > 0;
    const imageFileAnchor =
      tag === 'a' &&
      !lightboxHost &&
      !insideLightbox &&
      !hasImgChild &&
      isSafeSystemMessageHref(href) &&
      hrefLooksLikeImageFile(href);
    const navAnchor =
      tag === 'a' &&
      !lightboxHost &&
      !imageFileAnchor &&
      isSafeSystemMessageHref(href);
    const childNodes = Array.from(el.childNodes).map((child, childIndex) =>
      renderNode(
        child,
        childIndex,
        insideLightbox || lightboxHost || imageFileAnchor,
        insideAnchor || navAnchor,
      ),
    );

    if (lightboxHost) {
      const src = lightboxSrcFrom(el);
      return (
        <button
          type="button"
          key={index}
          className={LIGHTBOX_BUTTON_CLASS}
          data-sysmsg-lightbox="1"
          data-sysmsg-src={src}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onOpenImage(src);
          }}
        >
          {childNodes}
        </button>
      );
    }

    if (tag === 'img') {
      const imgSrc = el.getAttribute('src') ?? '';
      const img = (
        <img
          src={imgSrc}
          alt={el.getAttribute('alt') ?? ''}
          style={parseMaxBoxStyle(el.getAttribute('style'))}
          className={
            insideAnchor
              ? 'max-w-full cursor-pointer object-contain'
              : 'max-w-full cursor-zoom-in object-contain'
          }
        />
      );
      if (insideLightbox || insideAnchor || !isSafeSystemMessageImageUrl(imgSrc)) {
        return <span key={index}>{img}</span>;
      }
      return (
        <button
          type="button"
          key={index}
          className="inline-block max-w-full cursor-zoom-in touch-manipulation bg-transparent p-0"
          data-sysmsg-lightbox="1"
          data-sysmsg-src={imgSrc}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onOpenImage(imgSrc);
          }}
        >
          {img}
        </button>
      );
    }

    if (tag === 'a') {
      if (insideLightbox || !isSafeSystemMessageHref(href)) {
        return <span key={index}>{childNodes}</span>;
      }
      if (imageFileAnchor) {
        return (
          <button
            type="button"
            key={index}
            className={LIGHTBOX_BUTTON_CLASS}
            data-sysmsg-lightbox="1"
            data-sysmsg-src={href}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onOpenImage(href);
            }}
          >
            {childNodes}
          </button>
        );
      }
      const linkText = el.textContent?.trim() ?? '';
      return (
        <a
          key={index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={hasImgChild ? 'inline-block max-w-full cursor-pointer' : undefined}
          aria-label={linkText === '' ? t('systemMessage.image.openLinkAria') : undefined}
          tabIndex={0}
        >
          {childNodes}
        </a>
      );
    }

    if (tag === 'br') {
      return <br key={index} />;
    }

    if (tag === 'p') {
      return <p key={index}>{childNodes}</p>;
    }
    if (tag === 'h2') {
      return <h2 key={index}>{childNodes}</h2>;
    }
    if (tag === 'h3') {
      return <h3 key={index}>{childNodes}</h3>;
    }
    if (tag === 'blockquote') {
      return <blockquote key={index}>{childNodes}</blockquote>;
    }
    if (tag === 'ul') {
      return <ul key={index}>{childNodes}</ul>;
    }
    if (tag === 'ol') {
      return <ol key={index}>{childNodes}</ol>;
    }
    if (tag === 'li') {
      return <li key={index}>{childNodes}</li>;
    }
    if (tag === 'strong') {
      return <strong key={index}>{childNodes}</strong>;
    }
    if (tag === 'b') {
      return <b key={index}>{childNodes}</b>;
    }
    if (tag === 'em') {
      return <em key={index}>{childNodes}</em>;
    }
    if (tag === 'i') {
      return <i key={index}>{childNodes}</i>;
    }
    if (tag === 'u') {
      return <u key={index}>{childNodes}</u>;
    }
    if (tag === 'span') {
      return <span key={index}>{childNodes}</span>;
    }
    return <span key={index}>{childNodes}</span>;
  };

  return (
    <div
      ref={containerRef}
      className="prose prose-sm max-w-none overflow-x-auto text-gray-700 dark:prose-invert dark:text-gray-200 [&_button_a]:pointer-events-none [&_img]:max-w-full"
    >
      {rootNodes.length === 0 ? (
        <p>{emptyFallback}</p>
      ) : (
        rootNodes.map((node, index) => renderNode(node, index, false, false))
      )}
    </div>
  );
};
